import React, { useEffect, useState } from "react";
import { userService } from "../services/userService";
import {
  LuArrowRight,
  LuClock3,
} from "react-icons/lu";
import vendorDashboardController from "../controllers/vendorDashboardController";
import VendorLayout from "./VendorLayout";
import { translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";


const getStatusClassName = (status) =>
  String(status || "unknown")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "unknown";

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent);

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? {};

const formatCount = (value) => {
  if (!isPresent(value)) return "-";
  const numberValue = Number(value);
  return Number.isNaN(numberValue)
    ? String(value)
    : numberValue.toLocaleString();
};

const formatRating = (value) => {
  if (!isPresent(value)) return "-";
  const numberValue = Number(value);

  return Number.isNaN(numberValue)
    ? String(value)
    : numberValue.toLocaleString(undefined, {
        maximumFractionDigits: 1,
      });
};

const getAmountValue = (value) => {
  if (!value || typeof value !== "object") return value;

  return firstDefined(
    value.amount,
    value.value,
    value.total,
    value.totalAmount,
  );
};

const formatCurrency = (value, currency = "USD") => {
  if (!isPresent(value)) return "-";

  const amountValue = getAmountValue(value);
  const numberValue = Number(amountValue);

  if (Number.isNaN(numberValue)) return String(amountValue);

  const currencyCode = String(currency || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      currency: currencyCode,
      maximumFractionDigits: numberValue % 1 === 0 ? 0 : 2,
      style: "currency",
    }).format(numberValue);
  } catch {
    return `$${numberValue.toLocaleString(undefined, {
      maximumFractionDigits: numberValue % 1 === 0 ? 0 : 2,
    })}`;
  }
};

const formatReviewDetail = (value) => {
  if (!isPresent(value)) return "Reviews unavailable";
  return `${formatCount(value)} reviews`;
};

const formatDateLabel = (value) => {
  if (!isPresent(value)) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString(undefined, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
  });
};

const getOrdersList = (payload) => {
  const orders = firstDefined(
    payload.recentOrders,
    payload.latestOrders,
    payload.orders,
    payload.orderHistory,
    payload.recentOrderList,
    payload.dashboardOrders,
  );

  if (Array.isArray(orders)) return orders;
  if (Array.isArray(orders?.content)) return orders.content;
  if (Array.isArray(orders?.items)) return orders.items;

  return [];
};

const getCustomerName = (order) => {
  const directName = firstDefined(
    order.customerName,
    order.customer,
    order.clientName,
    order.userName,
  );

  if (directName) return directName;

  const firstName = firstDefined(order.customerFirstName, order.firstName);
  const lastName = firstDefined(order.customerLastName, order.lastName);

  return [firstName, lastName].filter(Boolean).join(" ") || "Customer";
};

const getItemsLabel = (order) => {
  const itemCount = firstDefined(
    order.itemsCount,
    order.itemCount,
    order.numberOfItems,
    order.quantity,
  );

  if (isPresent(itemCount)) {
    const numberValue = Number(itemCount);
    const suffix = numberValue === 1 ? "item" : "items";
    return `${formatCount(itemCount)} ${suffix}`;
  }

  if (Array.isArray(order.items)) {
    const suffix = order.items.length === 1 ? "item" : "items";
    return `${order.items.length} ${suffix}`;
  }

  if (typeof order.items === "string") return order.items;

  return "Items unavailable";
};

const normalizeOrders = (payload) =>
  getOrdersList(payload).map((order, index) => {
    const currency = firstDefined(order.currency, payload.currency, "USD");
    const orderId = firstDefined(
      order.orderNumber,
      order.orderNo,
      order.orderId,
      order.orderID,
      order.id,
      index + 1,
    );

    return {
      customer: getCustomerName(order),
      id: String(orderId).startsWith("#") ? String(orderId) : `#${orderId}`,
      items: getItemsLabel(order),
      status: firstDefined(order.status, order.orderStatus, order.state, "-"),
      time: firstDefined(
        order.timeAgo,
        order.elapsedTime,
        order.createdAgo,
        formatDateLabel(
          firstDefined(
            order.createdAt,
            order.creationDate,
            order.orderDate,
            order.date,
          ),
        ),
        "-",
      ),
      total: formatCurrency(
        firstDefined(
          order.total,
          order.totalAmount,
          order.amount,
          order.orderTotal,
          order.price,
        ),
        currency,
      ),
    };
  });

const normalizeDashboard = (payload) => {
  const overview =
    payload.overview ??
    payload.summary ??
    payload.dashboard ??
    payload.vendorDashboard ??
    payload;
  const revenue = firstDefined(
    overview.monthlyRevenue,
    overview.revenue,
    overview.monthRevenue,
    overview.totalMonthlyRevenue,
  );
  const currency = firstDefined(
    overview.currency,
    payload.currency,
    revenue?.currency,
    "USD",
  );

  return {
    orders: normalizeOrders(payload),
    statCards: [
      {
        detail: formatReviewDetail(
          firstDefined(
            overview.reviewCount,
            overview.reviews,
            overview.totalReviews,
            overview.numberOfReviews,
          ),
        ),
        label: "Store Health",
        value: formatRating(
          firstDefined(
            overview.storeHealth,
            overview.rating,
            overview.averageRating,
            overview.avgRating,
            overview.healthScore,
          ),
        ),
      },
      {
        detail: firstDefined(
          overview.month,
          overview.revenueMonth,
          overview.period,
          "Current month",
        ),
        label: "Monthly Revenue",
        value: formatCurrency(revenue, currency),
      },
      {
        detail: firstDefined(
          overview.dailyOrdersDetail,
          overview.ordersDetail,
          "Today",
        ),
        label: "Daily Orders",
        value: formatCount(
          firstDefined(
            overview.dailyOrders,
            overview.todayOrders,
            overview.ordersToday,
            overview.dailyOrderCount,
            overview.orderCountToday,
          ),
        ),
      },
    ],
  };
};

function VendorDashboard() {
  useTranslationSnapshot();
  const [statCards, setStatCards] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchDashboard = async () => {
      setLoading(true);
      setError("");

      try {
        const Authorization = userService.getJwt();
        const companyId = userService.getUserData().Compid;

        if (!companyId) {
          throw new Error("Company id was not found for this user.");
        }

        const response = await vendorDashboardController.getVendorDashboard({
          Authorization,
          companyId,
        });

        if (response?.isAxiosError || response?.response) {
          throw response;
        }

        const dashboard = normalizeDashboard(unwrapPayload(response));

        if (!isCurrentRequest) return;

        setStatCards(dashboard.statCards);
        setRecentOrders(dashboard.orders);
      } catch (requestError) {
        if (!isCurrentRequest) return;

        setStatCards([]);
        setRecentOrders([]);
        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load dashboard.",
        );
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  return (
    <VendorLayout activeKey="dashboard">
      <section className="vendor-dashboard">
        <div className="vendor-dashboard-header">
          <div className="vendor-dashboard-title">{translateLabel("txt_dashboard", "Dashboard")}</div>
          <div className="vendor-dashboard-subtitle">
            {translateLabel("txt_dashboard_description", "Overview of your business performance and activity")}
          </div>
        </div>

        {loading && (
          <div className="vendor-dashboard-state">{translateLabel("txt_business_loading_dashboard", "Loading dashboard...")}</div>
        )}
        {!loading && error && (
          <div className="vendor-dashboard-state vendor-dashboard-state-error">
            {translateText(error)}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="vendor-dashboard-stats">
              {statCards.map((card) => (
                <article className="vendor-dashboard-stat-card" key={card.label}>
                  <span className="vendor-dashboard-stat-label">{translateText(card.label)}</span>
                  <strong>{card.value}</strong>
                  <span className="vendor-dashboard-stat-detail">
                    {card.detail}
                  </span>
                </article>
              ))}
            </div>

            <section className="vendor-recent-orders">
              <div className="vendor-recent-orders-header">
                <div>
                  <div className="vendor-recent-orders-title">
                    {translateLabel("txt_recent_orders", "Recent Orders")}
                  </div>
                  <div className="vendor-recent-orders-subtitle">
                    {translateLabel("txt_recent_orders_description", "Latest customer orders with status updates")}
                  </div>
                </div>

                <button className="vendor-view-all-button" type="button"> {translateLabel("lnk_view_all", "View All")} <LuArrowRight className="navigation-direction-icon" aria-hidden="true" />
                </button>
              </div>

              <div className="vendor-order-list">
                {recentOrders.length === 0 && (
                  <div className="vendor-dashboard-empty-state">
                    {translateLabel("txt_no_recent_orders", "No recent orders found.")}
                  </div>
                )}
                {recentOrders.map((order) => (
                  <article className="vendor-order-card" key={order.id}>
                    <div className="vendor-order-customer">
                      <strong>{order.id}</strong>
                      <span>{order.customer}</span>
                      <small>
                        <LuClock3 aria-hidden="true" />
                        {order.time}
                      </small>
                    </div>

                    <div className="vendor-order-summary">
                      <span
                        className={`vendor-order-status vendor-order-status-${getStatusClassName(
                          order.status,
                        )}`}
                      >
                        {order.status}
                      </span>
                      <strong>{order.total}</strong>
                      <small>{order.items}</small>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </section>
    </VendorLayout>
  );
}

export default VendorDashboard;
